import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gem, CheckCircle2, Landmark, Upload, Clock, Copy, ShieldCheck } from "lucide-react";

const PASOS = [
  "Transfiere $299 MXN a la cuenta BBVA indicada (concepto: nombre de tu comercio).",
  "Toma foto o captura del comprobante y súbelo aquí con la referencia.",
  "El administrador lo revisa y activa tu plan; recibirás una notificación.",
  "Tus productos y cupones se publican de inmediato por 30 días. Renueva antes de vencer.",
];

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const img = new Image(); const url = URL.createObjectURL(file);
  img.onload = () => {
    const max = 1400; const s = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas"); c.width = img.width * s; c.height = img.height * s;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url); res(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = rej; img.src = url;
});

export const MiPlanTab = () => {
  const [data, setData] = useState(null);
  const [img, setImg] = useState("");
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/suscripcion/mia").then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enviar = async (e) => {
    e.preventDefault();
    if (!img) { toast.error("Sube la imagen de tu comprobante"); return; }
    setLoading(true);
    try {
      await api.post("/suscripcion/comprobante", { comprobante_url: img, referencia: ref, monto: data.plan.precio });
      toast.success("Comprobante enviado. Te avisamos al activarlo."); setImg(""); setRef(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (!data) return <div className="p-10 text-center text-zinc-400">Cargando…</div>;
  const { plan, banco } = data;
  const copiar = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiado"); };

  return (
    <div className="space-y-6" data-testid="mi-plan-tab">
      <div className={`rounded-sm p-6 text-white grid lg:grid-cols-[1fr_auto] gap-6 items-center ${data.activo ? "bg-zinc-900" : "bg-[#FF3366]"}`}>
        <div>
          <div className="overline text-white/70 flex items-center gap-1"><Gem className="w-3.5 h-3.5" /> {plan.nombre}</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1" data-testid="plan-estado">
            {data.activo ? `Plan activo · vence ${data.vence?.slice(0, 10)}` : data.pendiente ? "Comprobante en revisión" : "Plan inactivo · tus productos no se publican"}
          </div>
          <p className="text-white/80 text-sm mt-1">{data.activo ? `${data.dias_restantes} días restantes. Puedes renovar anticipadamente y se suman 30 días.` : "Activa tu plan para publicar tu tienda online, aparecer en el e-commerce general y hacer marketing con cupones."}</p>
        </div>
        <div className="bg-white/10 rounded-sm px-6 py-4 text-center">
          <div className="font-display font-black text-4xl tracking-tighter">${plan.precio.toFixed(0)}<span className="text-base font-medium"> {plan.moneda}</span></div>
          <div className="text-[10px] uppercase tracking-wider text-white/70">por {plan.dias} días</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="overline text-zinc-500 mb-3">Todo lo que incluye</div>
          <ul className="space-y-2.5">
            {plan.beneficios.map(b => (
              <li key={b.t} className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span><b>{b.t}.</b> <span className="text-zinc-600">{b.d}</span></span></li>
            ))}
          </ul>
        </div>

        <div className="space-y-4">
          <div className="bg-[#D4FF00] rounded-sm p-5">
            <div className="font-display font-black text-xl tracking-tight flex items-center gap-2"><Landmark className="w-5 h-5" /> Paga por transferencia</div>
            <div className="mt-3 space-y-1.5 text-sm">
              {[["Titular", banco.titular], ["Banco", banco.banco], ["CLABE", banco.clabe], ["Monto", `$${plan.precio.toFixed(2)} MXN`]].map(([l, v]) => (
                <div key={l} className="flex justify-between items-center border-b border-zinc-900/10 pb-1"><span className="text-zinc-700">{l}</span><span className={`font-medium flex items-center gap-2 ${l === "CLABE" ? "font-mono" : ""}`} data-testid={l === "CLABE" ? "plan-clabe" : undefined}>{v}<button type="button" onClick={() => copiar(v)}><Copy className="w-3.5 h-3.5 text-zinc-600" /></button></span></div>
              ))}
            </div>
            <ol className="mt-4 space-y-1 text-xs text-zinc-800">{PASOS.map((p, i) => <li key={`pp-${i}`}><b>{i + 1}.</b> {p}</li>)}</ol>
          </div>

          <form onSubmit={enviar} className="bg-white border border-zinc-200 rounded-sm p-5 space-y-3" data-testid="comprobante-form">
            <div className="font-display font-bold tracking-tight flex items-center gap-2"><Upload className="w-4 h-4 text-[#FF3366]" /> Enviar comprobante de pago</div>
            <label className="block border-2 border-dashed border-zinc-300 rounded-sm p-4 text-center cursor-pointer hover:border-zinc-900">
              <input data-testid="comprobante-file" type="file" accept="image/*" className="hidden" onChange={async e => { const f = e.target.files?.[0]; if (f) setImg(await fileToDataUrl(f)); }} />
              {img ? <img src={img} alt="comprobante" className="max-h-48 mx-auto rounded-sm" data-testid="comprobante-preview" /> : <span className="text-sm text-zinc-500">Toca para subir foto o captura del comprobante</span>}
            </label>
            <Input data-testid="comprobante-ref" value={ref} onChange={e => setRef(e.target.value)} placeholder="Referencia / folio de la transferencia (opcional)" className="rounded-sm" />
            <Button type="submit" data-testid="comprobante-enviar" disabled={loading || data.pendiente} className="w-full h-11 rounded-sm bg-zinc-900 hover:bg-[#FF3366]">
              {data.pendiente ? <><Clock className="w-4 h-4 mr-2" /> Comprobante en revisión</> : loading ? "Enviando…" : "Enviar comprobante al administrador"}
            </Button>
          </form>
        </div>
      </div>

      {data.pagos.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="overline text-zinc-500 mb-2">Historial de pagos</div>
          <div className="divide-y divide-zinc-100 text-sm">
            {data.pagos.map(p => (
              <div key={p.id} data-testid={`pago-${p.id}`} className="flex items-center gap-3 py-2">
                <ShieldCheck className="w-4 h-4 text-zinc-400" />
                <span className="flex-1">{new Date(p.created_at).toLocaleString()} · ${p.monto} {p.referencia ? `· ref ${p.referencia}` : ""}{p.motivo ? ` · ${p.motivo}` : ""}</span>
                <Badge className={`border-0 ${p.estado === "aprobado" ? "bg-emerald-100 text-emerald-700" : p.estado === "rechazado" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{p.estado}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
